import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Avatar } from 'primereact/avatar';
import { tasksAPI } from '../services/api';
import { Task } from '../types';
import TaskDetailSidebar from './TaskDetailSidebar';
import TaskModal from './TaskModal';
import { showCustomAlert } from './CustomAlert';

interface ProjectTasksCalendarProps {
  projectId: string;
}

const ProjectTasksCalendar: React.FC<ProjectTasksCalendarProps> = ({ projectId }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    loadTasks();
  }, [projectId]);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const data = await tasksAPI.getAll({ projectId });
      const tasksList = Array.isArray(data) ? data : (data as any).items || [];
      setTasks(tasksList);
    } catch (err: any) {
      console.error('Error loading tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = () => {
    setEditingTask(null);
    setTaskModalVisible(true);
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setTaskModalVisible(true);
  };

  const handleDeleteTask = async (task: Task) => {
    showCustomAlert.confirm(
      'Da li ste sigurni da želite da obrišete zadatak?',
      async () => {
        try {
          await tasksAPI.delete(task._id);
          await loadTasks();
        } catch (err: any) {
          console.error('Error deleting task:', err);
          showCustomAlert.error('Greška pri brisanju zadatka');
        }
      },
      'Potvrda brisanja'
    );
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const firstDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    
    const days = [];
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getTasksForDay = (day: number | null) => {
    if (day === null) return [];
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return tasks.filter(task => {
      if (!task.deadline) return false;
      const taskDate = new Date(task.deadline);
      return taskDate.getDate() === day &&
             taskDate.getMonth() === currentMonth.getMonth() &&
             taskDate.getFullYear() === currentMonth.getFullYear();
    });
  };

  const days = getDaysInMonth(currentMonth);
  const monthName = currentMonth.toLocaleDateString('sr-RS', { month: 'long', year: 'numeric' });
  const weekDays = ['Ned', 'Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub'];

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentMonth);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentMonth(newDate);
  };

  const priorityColors: Record<string, string> = {
    low: '#6b7280',
    medium: '#f59e0b',
    high: '#ef4444'
  };

  return (
    <div>
      <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Button
            icon="pi pi-chevron-left"
            className="p-button-text"
            onClick={() => navigateMonth('prev')}
          />
          <h2 style={{ margin: 0, minWidth: '200px', textAlign: 'center' }}>{monthName}</h2>
          <Button
            icon="pi pi-chevron-right"
            className="p-button-text"
            onClick={() => navigateMonth('next')}
          />
        </div>
        <Button
          label="Add task"
          icon="pi pi-plus"
          className="p-button-success"
          onClick={handleAddTask}
        />
      </div>

      <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        {/* Week day headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.5rem', marginBottom: '0.5rem' }}>
          {weekDays.map(day => (
            <div key={day} style={{ textAlign: 'center', fontWeight: 'bold', padding: '0.5rem', color: '#666' }}>
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.5rem' }}>
          {days.map((day, index) => {
            const dayTasks = getTasksForDay(day);
            
            return (
              <div
                key={index}
                style={{
                  minHeight: '120px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  padding: '0.5rem',
                  backgroundColor: day === null ? '#f9fafb' : 'white',
                  position: 'relative'
                }}
              >
                {day !== null && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontWeight: 'bold', fontSize: '0.875rem' }}>{day}.</span>
                      {dayTasks.length > 0 && (
                        <Tag value={dayTasks.length} severity="info" style={{ fontSize: '0.75rem', padding: '0.125rem 0.375rem' }} />
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      {dayTasks.slice(0, 3).map(task => (
                        <div
                          key={task._id}
                          onClick={() => handleTaskClick(task)}
                          style={{
                            backgroundColor: priorityColors[task.priority] || '#9ca3af',
                            color: 'white',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            borderLeft: '3px solid',
                            borderLeftColor: priorityColors[task.priority] || '#9ca3af'
                          }}
                          title={task.name}
                        >
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                            {task.name.length > 15 ? `${task.name.substring(0, 15)}...` : task.name}
                          </span>
                          {task.assignees.length > 0 && (
                            <div style={{ display: 'flex', gap: '0.125rem' }}>
                              {task.assignees.slice(0, 2).map(assignee => (
                                <div
                                  key={assignee._id}
                                  style={{
                                    width: '16px',
                                    height: '16px',
                                    borderRadius: '50%',
                                    backgroundColor: 'rgba(255,255,255,0.3)',
                                    border: '1px solid white'
                                  }}
                                  title={`${assignee.firstName} ${assignee.lastName}`}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                      {dayTasks.length > 3 && (
                        <div style={{ fontSize: '0.75rem', color: '#666', textAlign: 'center' }}>
                          +{dayTasks.length - 3} više
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {selectedTask && (
        <TaskDetailSidebar
          task={selectedTask}
          visible={!!selectedTask}
          onHide={() => setSelectedTask(null)}
          onTaskUpdate={loadTasks}
        />
      )}

      {taskModalVisible && (
        <TaskModal
          visible={taskModalVisible}
          onHide={() => {
            setTaskModalVisible(false);
            setEditingTask(null);
          }}
          projectId={projectId}
          task={editingTask}
          onSave={loadTasks}
        />
      )}
    </div>
  );
};

export default ProjectTasksCalendar;
